<template>
  <v-col cols="12" class="pa-0">
    <v-row no-gutters>
      <admin-wrapper
        ref="ils-easy-settings-data-form"
        hide-title-row
        :inputs="inputs"
        :is-custom-saving="loading"
        disable-sticky-buttons
        :save-custom-validation="!syncing"
        @setPayloadValue="setPayload"
        @save="onSave"
      >
        <template #symbolizationLayers.input>
          <v-col cols="8">
            <card-title-row is-subtitle :has-icon-column="false" translationPath="admin.ilsEasy.symbolizationLayers" />
            <v-row no-gutters v-for="index of Array.from(Array(3).keys())" :key="index" class="pt-7">
              <v-col cols="4" class="pr-3">
                <data-input
                  v-model="computedConfiguration[`symbolization_layers_${index + 1}`].name"
                  v-bind="{
                    ...symbolizationLayerNameInput,
                    label: $i18n.t('admin.ilsEasy.symbolizationName', { index: index + 1 }),
                  }"
                />
              </v-col>
              <v-col cols="4">
                <data-input
                  v-model="computedConfiguration[`symbolization_layers_${index + 1}`].current"
                  v-bind="{ ...symbolizationLayerLayerInput, label: $i18n.t('admin.ilsEasy.currentLayer') }"
                />
              </v-col>
              <v-col cols="4" class="pl-3">
                <data-input
                  v-model="computedConfiguration[`symbolization_layers_${index + 1}`].masterplan"
                  v-bind="{ ...symbolizationLayerLayerInput, label: $i18n.t('admin.ilsEasy.masterplanLayer') }"
                />
              </v-col>
            </v-row>
          </v-col>
        </template>
        <template #projects.input="data">
          <v-col cols="3">
            <data-input v-bind="data" v-model="computedConfiguration['predefined_maps']">
              <template #selection="{ item, index }">
                <v-tooltip v-if="index <= 1" bottom color="font">
                  <template #activator="{ on }">
                    <v-chip v-on="on" x-small>
                      <div class="text-truncate">{{ item.text }}</div>
                    </v-chip>
                  </template>
                  {{ item.text }}
                </v-tooltip>
                <div v-if="index === 2" class="grey--text caption" style="white-space: nowrap !important;">
                  {{ `(+${$i18n.tc('admin.other', computedConfiguration.predefined_maps.length - 2)})` }}
                </div>
              </template>
            </data-input>
          </v-col>
        </template>
        <template #customMenus.input="data">
          <v-col cols="12">
            <card-title-row
              class="pb-5"
              translation-path="admin.customMenus.navbar"
              :has-icon-column="false"
              :has-buttons="false"
            />
            <custom-menus-draggable-list ref="custom-menus-draggable-list" v-bind="data" />
          </v-col>
        </template>
      </admin-wrapper>
    </v-row>
    <v-row no-gutters class="pt-8">
      <ils-easy-settings-search-objects :disabled="syncing" />
    </v-row>
    <v-row no-gutters class="pt-8">
      <ils-easy-settings-layers :disabled="syncing" />
    </v-row>
  </v-col>
</template>
<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import CardTitleRow from '@/components/CardTitleRow';
import CustomMenusDraggableList from '@/components/CustomMenusDraggableList';
import DataInput from '@/components/DataInput';
import IlsEasySettingsLayers from '@/components/IlsEasySettingsLayers';
import IlsEasySettingsSearchObjects from '@/components/IlsEasySettingsSearchObjects';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
export default {
  name: 'IlsEasySettingsData',
  components: {
    AdminWrapper,
    CardTitleRow,
    CustomMenusDraggableList,
    DataInput,
    IlsEasySettingsLayers,
    IlsEasySettingsSearchObjects,
  },
  mixins: [prepareAttributesFeatureCardMixin],
  props: {
    configuration: {
      type: Object,
      default: () => {
        return {};
      },
    },
    syncing: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    projects: get('layers/projects'),
    predefinedMappedProjects() {
      return this.$_objectToArray(this.projects)
        .filter(project => project.role === 'predefined')
        .map(project => {
          const { id, name } = project;
          return { value: id, text: name };
        });
    },
    dataSources: get('layers/metadata'),
    layers: get('layers/layers'),
    featuresLayers() {
      return this.$_objectToArray(this.layers)
        ?.filter(layer => layer.type === 'features_layer' && layer.layer_scope === 'core')
        ?.map(layer => {
          const { id, name } = layer;
          return { value: id, text: name };
        });
    },
    serviceLayers() {
      return this.$_objectToArray(this.layers)
        ?.filter(layer => layer.type === 'service_layer' && layer.layer_scope === 'core')
        ?.map(layer => {
          const { id, name } = layer;
          return { value: id, text: name };
        });
    },
    streetLampLayers() {
      return this.$_objectToArray(this.layers)
        ?.filter(layer => layer.data_source_name === this.streetLampLayer?.data_source_name)
        .map(layer => {
          const { id, name } = layer;
          return { value: id, text: name };
        });
    },
    streetLampLayer() {
      return this.layers[this.computedConfiguration.street_lamp_id] || null;
    },
    streetLampLayerAttributes() {
      return this.streetLampLayer?.form_schema?.elements || [];
    },
    streetLampDataSourceAttributes() {
      return this.dataSources[this.streetLampLayer?.data_source_name]?.attributes_schema?.attributes || [];
    },
    attributes() {
      return this.$_flattenStructure(this.getForm(this.streetLampLayerAttributes, this.streetLampDataSourceAttributes))
        ?.filter(attribute => attribute.dataType.name === 'text')
        ?.map(attribute => {
          return { text: attribute.label, value: attribute.attribute };
        });
    },
    streetLampAttributes() {
      return [
        {
          name: 'streetAttribute',
          key: 'street_attribute',
          type: 'attribute',
        },
        {
          name: 'districtAttribute',
          key: 'district_attribute',
          type: 'attribute',
        },
      ];
    },
    inputs() {
      const inputs = [
        {
          name: 'streetLamp',
          key: 'street_lamp_id',
          type: 'layer',
          layerType: 'features',
        },
        ...this.streetLampAttributes,
        {
          name: 'basemapDaylight',
          key: 'basemap_daylight_id',
          type: 'layer',
          layerType: 'service',
        },
        {
          name: 'basemapNightlight',
          key: 'basemap_nightlight_id',
          type: 'layer',
          layerType: 'service',
        },
        {
          name: 'basemapSatellite',
          key: 'basemap_satellite_id',
          type: 'layer',
          layerType: 'service',
        },
        {
          name: 'projects',
          key: 'predefined_maps',
          type: 'projects',
        },
        {
          name: 'ils_easy_configuration',
          valueSubproperty: 'streetview',
          translationPath: 'admin.ilsEasy.streetview',
          clearable: true,
          dataType: {
            name: 'select',
          },
          readonly: this.loading || this.syncing,
          items: [
            {
              value: 'streetview',
              text: 'Streetview',
            },
            // {
            //   value: 'streetsmart',
            //   text: 'Streetsmart',
            // },
          ],
          isCustom: true,
        },
        {
          name: 'symbolizationLayers',
          isNamedSlot: false,
        },
        {
          name: 'customMenus',
          titlePath: 'admin.customMenus.title',
          key: 'custom_menus',
          isNamedSlot: false,
          customMenus: this.configuration.custom_app_menu || [],
          disableRelativePath: true,
        },
      ];
      return inputs.map(input => {
        if (input.type === 'layer') {
          return {
            name: 'ils_easy_configuration',
            valueSubproperty: input.key,
            translationPath: `admin.ilsEasy.${input.name}`,
            rules: [v => !!v || v === 0 || 'rules.required'],
            dataType: {
              name: 'select',
            },
            readonly: this.loading || this.syncing,
            items: this[`${input.layerType}Layers`],
            isCustom: true,
          };
        } else if (input.type === 'attribute') {
          return {
            name: 'ils_easy_configuration',
            valueSubproperty: input.key,
            translationPath: `admin.ilsEasy.${input.name}`,
            rules: [v => !!v || v === 0 || 'rules.required'],
            dataType: {
              name: 'select',
            },
            readonly: this.loading || this.syncing,
            items: this.attributes,
            isCustom: true,
          };
        } else if (input.type === 'projects') {
          return {
            name: input.name,
            valueSubproperty: input.key,
            translationPath: `admin.ilsEasy.${input.name}`,
            rules: [v => !!v?.length || 'rules.required'],
            dataType: {
              name: 'select',
            },
            multiple: true,
            readonly: this.loading || this.syncing,
            items: this.predefinedMappedProjects,
            isCustom: true,
          };
        } else return input;
      });
    },
    symbolizationLayerNameInput() {
      return {
        dataType: { name: 'text' },
        readonly: this.loading || this.syncing,
        hideDetails: true,
      };
    },
    symbolizationLayerLayerInput() {
      return {
        dataType: { name: 'select' },
        readonly: this.loading || this.syncing,
        items: this.streetLampLayers,
        hideDetails: true,
      };
    },
    computedConfiguration: {
      get() {
        return this.configuration;
      },
      set(value) {
        this.$emit('update:configuration', value);
      },
    },
  },
  data: () => ({
    loading: false,
  }),
  methods: {
    editSingleSetting: call('admin/editSingleSetting'),
    setPayload({ value, subproperty }) {
      this.computedConfiguration = { ...this.computedConfiguration, [subproperty]: value };
    },
    async onSave() {
      const menus = this.$refs['custom-menus-draggable-list']?.items;
      this.loading = true;
      try {
        await this.editSingleSetting({
          key: 'ils_easy_configuration',
          body: { value: { ...this.computedConfiguration, custom_app_menu: menus } },
        });
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    'computedConfiguration.street_lamp_id': {
      handler(nV, oV) {
        if (!oV && nV) return;
        this.streetLampAttributes
          .map(attribute => attribute.key)
          .forEach(attribute => {
            this.$set(this.computedConfiguration, attribute, null);
            const inputIndex = this.inputs.findIndex(input => input.valueSubproperty === attribute);
            if (this.$refs['ils-easy-settings-data-form']?.$refs.form?.$refs.inputs[inputIndex]?.$refs.input) {
              this.$refs['ils-easy-settings-data-form'].$refs.form.$refs.inputs[inputIndex].$refs.input.value = null;
            }
          });
        this.computedConfiguration = {
          ...this.computedConfiguration,
          symbolization_layers_1: {
            name: '',
            current: null,
            masterplan: null,
          },
          symbolization_layers_2: {
            name: '',
            current: null,
            masterplan: null,
          },
          symbolization_layers_3: {
            name: '',
            current: null,
            masterplan: null,
          },
        };
      },
    },
  },
};
</script>
